import { App as AntdApp, Button, Modal } from 'antd';
import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { findModalInstance, generateModalId, initialModalState, modalReducer } from './reducer';
import {
  CloseReason,
  ModalContextValue,
  ModalFormContract,
  ModalInjectedProps,
  ModalInstance,
  ModalInstanceApi,
  normalizeAutoClose,
  OpenFormModalFn,
  OpenFormOptions,
  OpenModalFn,
  OpenOptions,
} from './types';

/**
 * Modal context
 */
const ModalContext = createContext<ModalContextValue | null>(null);

/**
 * Hook to access modal context
 */
export function useModalContext(): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
}

/**
 * Modal Provider component
 */
export interface ModalProviderProps {
  children: React.ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [state, dispatch] = useReducer(modalReducer, initialModalState);
  const { modal } = AntdApp.useApp();

  const closeModal = useCallback(
    (id: string) => {
      const instance = findModalInstance(state.instances, id);
      if (!instance) return;

      // First set visible to false (triggers closing animation)
      dispatch({ type: 'SET_VISIBLE', payload: { id, visible: false } });

      // Remove from instances after animation
      setTimeout(() => {
        dispatch({ type: 'REMOVE_MODAL', payload: id });
      }, 300); // Ant Design modal animation duration
    },
    [state.instances]
  );

  const resolveModal = useCallback(
    <T,>(id: string, result?: T) => {
      const instance = findModalInstance<T>(state.instances, id);
      if (!instance) return;

      instance.promise.resolve(result);
      closeModal(id);
    },
    [state.instances, closeModal]
  );

  const rejectModal = useCallback(
    (id: string, err?: unknown) => {
      const instance = findModalInstance(state.instances, id);
      if (!instance) return;

      instance.promise.reject(err);
      closeModal(id);
    },
    [state.instances, closeModal]
  );

  const setModalLoading = useCallback((id: string, type: 'ok' | 'cancel', loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: { id, type, loading } });
  }, []);

  const closeAll = useCallback(() => {
    // Resolve all instances and close them
    state.instances.forEach((instance) => {
      instance.promise.resolve(undefined);
    });
    dispatch({ type: 'CLEAR_ALL' });
  }, [state.instances]);

  const confirm = useCallback(
    (opts: import('antd').ModalFuncProps): Promise<boolean> => {
      return new Promise((resolve) => {
        modal.confirm({
          ...opts,
          onOk: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });
    },
    [modal]
  );

  // Main open function
  const open: OpenModalFn = useCallback(
    <TProps extends Record<string, unknown>, TResult = void>(
      Component: React.ComponentType<TProps & ModalInjectedProps<TResult>>,
      props: TProps,
      options: OpenOptions<TResult> = {}
    ): Promise<TResult | undefined> => {
      return new Promise<TResult | undefined>((resolve, reject) => {
        const id = generateModalId();

        const instance: ModalInstance<TResult> = {
          id,
          Component: Component as React.ComponentType<Record<string, unknown>>,
          props: props as Record<string, unknown>,
          visible: true,
          promise: { resolve, reject },
          state: { okLoading: false, cancelLoading: false },
          options,
        };

        dispatch({ type: 'ADD_MODAL', payload: instance as ModalInstance });
      });
    },
    []
  );

  // Form-specific open function
  const openForm: OpenFormModalFn = useCallback(
    <TProps extends Record<string, unknown>, TValues, TResult = TValues>(
      Component: React.ComponentType<TProps & ModalFormContract<TValues, TResult> & ModalInjectedProps<TResult>>,
      props: TProps,
      options: OpenFormOptions<TValues, TResult> = {}
    ): Promise<TResult | undefined> => {
      return new Promise<TResult | undefined>((resolve, reject) => {
        const id = generateModalId();

        const instance: ModalInstance<TResult> = {
          id,
          Component: Component as React.ComponentType<Record<string, unknown>>,
          props: props as Record<string, unknown>,
          visible: true,
          promise: { resolve, reject },
          state: { okLoading: false, cancelLoading: false },
          options: {
            ...options,
            // Override onBeforeOk to trigger form onSubmit if configured
            onBeforeOk: options.onSubmit
              ? async (): Promise<'manual'> => {
                  // Default implementation will be handled by footer
                  return 'manual';
                }
              : options.onBeforeOk,
          },
          formOptions: {
            defaultValues: options.defaultValues as Record<string, unknown>,
            onSubmit: options.onSubmit as
              | ((values: Record<string, unknown>, api: ModalInstanceApi<TResult>) => void | Promise<void>)
              | undefined,
            onCancel: options.onCancel as
              | ((
                  ctx: { reason: CloseReason; values?: Record<string, unknown> },
                  api: ModalInstanceApi<TResult>
                ) => void | Promise<void>)
              | undefined,
          },
        };

        dispatch({ type: 'ADD_MODAL', payload: instance as ModalInstance });
      });
    },
    []
  );

  // Create context value
  const contextValue = useMemo<ModalContextValue>(
    () => ({
      instances: state.instances,
      open,
      openForm,
      confirm,
      closeAll,
      closeModal,
      resolveModal,
      rejectModal,
      setModalLoading,
    }),
    [state.instances, open, openForm, confirm, closeAll, closeModal, resolveModal, rejectModal, setModalLoading]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {state.instances.map((instance) => (
        <ModalInstanceRenderer key={instance.id} instance={instance} />
      ))}
    </ModalContext.Provider>
  );
}

/**
 * Component to render individual modal instances
 */
interface ModalInstanceRendererProps {
  instance: ModalInstance;
}

function ModalInstanceRenderer({ instance }: ModalInstanceRendererProps) {
  const { closeModal, resolveModal, rejectModal, setModalLoading } = useModalContext();
  const autoCloseConfig = normalizeAutoClose(instance.options.autoClose);

  // Create API for the modal instance
  const api: ModalInstanceApi = useMemo(
    () => ({
      id: instance.id,
      close: () => closeModal(instance.id),
      resolve: (result) => resolveModal(instance.id, result),
      reject: (err) => rejectModal(instance.id, err),
      setOkLoading: (loading) => setModalLoading(instance.id, 'ok', loading),
      setCancelLoading: (loading) => setModalLoading(instance.id, 'cancel', loading),
    }),
    [instance.id, closeModal, resolveModal, rejectModal, setModalLoading]
  );

  // Injected props for the modal component
  const injectedProps: ModalInjectedProps = {
    visible: instance.visible,
    resolve: api.resolve,
    reject: api.reject,
    close: api.close,
    setOkLoading: api.setOkLoading,
    setCancelLoading: api.setCancelLoading,
    api,
  };

  // Handle modal events
  const handleOk = useCallback(async () => {
    // Check if this is a form modal with form-specific onSubmit
    if (instance.formOptions?.onSubmit) {
      // For form modals, try to submit the form inside the modal
      try {
        // Look for a form element inside the modal and submit it
        const modalElement = document.querySelector(`[data-modal-id="${instance.id}"]`);
        const formElement = modalElement?.querySelector('form');

        if (formElement) {
          // Create a submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          formElement.dispatchEvent(submitEvent);
          return;
        }

        // Fallback to default behavior
        if (instance.options.onBeforeOk) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result = await instance.options.onBeforeOk(api as any);
          if (result === false) return;
          if (result === 'manual') return;
        }
      } catch (error) {
        // Handle form validation errors
        console.error('Form validation error:', error);
        return;
      }
    } else {
      // Standard modal behavior
      if (instance.options.onBeforeOk) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await instance.options.onBeforeOk(api as any);
        if (result === false) return;
        if (result === 'manual') return;
      }

      if (autoCloseConfig.ok) {
        api.resolve();
      }
    }
  }, [instance.options, instance.formOptions, instance.id, api, autoCloseConfig.ok]);

  const handleCancel = useCallback(async () => {
    // Check if this is a form modal with form-specific onCancel
    if (instance.formOptions?.onCancel) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await instance.formOptions.onCancel({ reason: { source: 'cancel' } }, api as any);
      } catch (error) {
        console.error('Form cancel error:', error);
      }
    }

    if (instance.options.onBeforeCancel) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await instance.options.onBeforeCancel(api as any);
      if (result === false) return;
      if (result === 'manual') return;
    }

    if (autoCloseConfig.cancel) {
      api.resolve(undefined);
    }
  }, [instance.options, instance.formOptions, api, autoCloseConfig.cancel]);

  // Render custom footer if provided
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let footer = instance.options.renderFooter ? instance.options.renderFooter(api as any) : undefined;

  // If footerButtons are provided, render custom footer with those buttons
  if (!footer && instance.options.footerButtons && instance.options.footerButtons.length > 0) {
    footer = (
      <div className="flex gap-2 justify-end">
        {instance.options.footerButtons.map((button) => (
          <Button
            key={button.key}
            type={button.type || 'default'}
            danger={button.danger}
            disabled={button.disabled}
            loading={
              button.loading ||
              (button.key === 'ok' && instance.state.okLoading) ||
              (button.key === 'cancel' && instance.state.cancelLoading)
            }
            onClick={async () => {
              if (button.onClick) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await button.onClick(api as any);
              }

              // Handle auto-close behavior
              if (button.autoClose) {
                if (button.key === 'ok') {
                  // For OK buttons, check if this is a form modal
                  if (instance.formOptions?.onSubmit) {
                    // Trigger form submission - this should be handled by the form component
                    // The form component will call the onSubmit callback when ready
                    return;
                  } else {
                    api.resolve();
                  }
                } else if (button.key === 'cancel') {
                  if (instance.formOptions?.onCancel) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    await instance.formOptions.onCancel({ reason: { source: 'cancel' } }, api as any);
                  }
                  api.close();
                } else {
                  // For other buttons, just close
                  api.close();
                }
              }
            }}
          >
            {button.label}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <Modal
      open={instance.visible}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={footer}
      confirmLoading={instance.state.okLoading}
      {...instance.options.modalProps}
      // Prevent closing on ESC/mask if disabled
      keyboard={autoCloseConfig.esc}
      maskClosable={autoCloseConfig.mask}
    >
      <div data-modal-id={instance.id}>
        <instance.Component
          {...instance.props}
          {...injectedProps}
          {...(instance.formOptions && {
            onSubmit: instance.formOptions.onSubmit,
            onCancel: instance.formOptions.onCancel,
          })}
        />
      </div>
    </Modal>
  );
}
